import { takeLatest, put, all, call } from "redux-saga/effects";
import * as Sentry from "@sentry/react";
import ApiCollections from "../../configs/services/apiCollections";
import AuthActionTypes from "./auth.types";
import * as AuthActions from "./auth.actions";
import { fetchEbayStatusRequest } from "../ebay/ebay.actions";
import { toastify } from "../../configs/utils";


/******************** Other Sagas ****************************/

function* signIn({ payload }) {
  yield put(AuthActions.loadingStart());

  try {
    const response = yield call(ApiCollections.login, payload?.formValues);
    
  
    // Store tokens in localStorage
    if (response?.tokens?.access) {
      localStorage.setItem("access", response.tokens.access);
    }
    if (response?.tokens?.refresh) {
      localStorage.setItem("refresh", response.tokens.refresh);
    }

    // Dispatch success with the response data (not just user)
    yield put(AuthActions.signInSuccess(response));

    // Check eBay status immediately after successful login
    yield put(fetchEbayStatusRequest());

    toastify("success", response?.message || "Logged In Successfully");
    if (payload?.func) {
      payload.func();
    }
  } catch (error) {
    console.error("Login error:", error);
    Sentry.withScope((scope) => {
      scope.setTag("saga", "signIn");
      scope.setLevel("error");
      Sentry.captureException(error);
    });
    yield put(AuthActions.signInFailure(error.response?.data));
    toastify("error", error.response?.data?.message || "Login failed");
  } finally {
    yield put(AuthActions.loadingStop());
  }
}

function* signOut({ payload }) {
  try {
    console.log("Logout saga started");
    localStorage.clear();
    console.log("localStorage cleared");
    
    if (payload?.func) {
      payload.func();
    }
    
    console.log("Dispatching SIGN_OUT_SUCCESS");
    
    yield put(AuthActions.signOutSuccess());
  // eslint-disable-next-line no-unused-vars
  } catch (error) {
    console.error("Logout error:", error);
    Sentry.withScope((scope) => {
      scope.setTag("saga", "signOut");
      scope.setLevel("error");
      Sentry.captureException(error);
    });
    toastify("error", "SignOut Failed");
    yield put(AuthActions.signOutFailure("SignOut Failed"));
  }
}

function* register({ payload }) {
  yield put(AuthActions.loadingStart());
  try {
    const response = yield call(ApiCollections.register, payload?.formValues);
   
    yield put(AuthActions.registerSuccess(response));

    toastify("success", "Registration successful. You can now sign in.");
    if (payload?.func) {
      payload.func();
    }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("saga", "register");
      scope.setLevel("error");
      Sentry.captureException(error);
    });
    yield put(AuthActions.registerFailure(error.response?.data));
    toastify("error", error.response?.data?.message || "Registration failed");
  } finally {
    yield put(AuthActions.loadingStop());
  }
}

function* registerInitiate({ payload }) {
  yield put(AuthActions.loadingStart());
  try {
    const response = yield call(ApiCollections.registerInitiate, payload?.formValues);
    
    yield put(AuthActions.registerInitiateSuccess(response));
    
    toastify("success", response?.message || "Verification link has been sent to your email");
    if (payload?.func) {
      payload.func(response);
    }
  } catch (error) {
    console.error("Register initiate error:", error);
    Sentry.withScope((scope) => {
      scope.setTag("saga", "registerInitiate");
      scope.setLevel("error");
      Sentry.captureException(error);
    });
    const errorData = error.response?.data;
    let errorMessage = "Failed to send verification email";
    
    // Handle error messages from API
    if (errorData?.errors) {
      if (errorData.errors.email) {
        errorMessage = errorData.errors.email[0];
      } else if (errorData.errors.non_field_errors) {
        errorMessage = errorData.errors.non_field_errors[0];
      }
    } else if (errorData?.message) {
      errorMessage = errorData.message;
    }
    
    yield put(AuthActions.registerInitiateFailure(errorData));
    toastify("error", errorMessage);
  } finally {
    yield put(AuthActions.loadingStop());
  }
}

function* registerComplete({ payload }) {
  yield put(AuthActions.loadingStart());
  try {
    const response = yield call(ApiCollections.registerComplete, payload?.formValues);
    
    // Store tokens in localStorage
    if (response?.tokens?.access) {
      localStorage.setItem("access", response.tokens.access);
    }
    if (response?.tokens?.refresh) {
      localStorage.setItem("refresh", response.tokens.refresh);
    }
    
    yield put(AuthActions.registerCompleteSuccess(response));
    
    toastify("success", response?.message || "Registration successful");
    if (payload?.func) {
      payload.func(response);
    }
  } catch (error) {
    console.error("Register complete error:", error);
    Sentry.withScope((scope) => {
      scope.setTag("saga", "registerComplete");
      scope.setLevel("error");
      Sentry.captureException(error);
    });
    const errorData = error.response?.data;
    let errorMessage = "Registration failed";
    
    // Handle error messages from API
    if (errorData?.errors) {
      // Check for field-specific errors
      if (errorData.errors.verification_token) {
        errorMessage = errorData.errors.verification_token[0];
      } else if (errorData.errors.username) {
        errorMessage = errorData.errors.username[0];
      } else if (errorData.errors.password) {
        errorMessage = errorData.errors.password[0];
      } else if (errorData.errors.password_confirm) {
        errorMessage = errorData.errors.password_confirm[0];
      } else if (errorData.errors.non_field_errors) {
        errorMessage = errorData.errors.non_field_errors[0];
      }
    } else if (errorData?.message) {
      errorMessage = errorData.message;
    }
    
    yield put(AuthActions.registerCompleteFailure(errorData));
    toastify("error", errorMessage);
  } finally {
    yield put(AuthActions.loadingStop());
  }
}



/******************** Root Saga ****************************/

export function* authSagas() {
  yield all([
    takeLatest(AuthActionTypes.SIGN_IN_REQUEST, signIn),
    takeLatest(AuthActionTypes.SIGN_OUT_REQUEST, signOut),
    takeLatest(AuthActionTypes.REGISTER_REQUEST, register),
    takeLatest(AuthActionTypes.REGISTER_INITIATE_REQUEST, registerInitiate),
    takeLatest(AuthActionTypes.REGISTER_COMPLETE_REQUEST, registerComplete),
  ]);
}
